<template>
	<app-page>
		<app-heading title="Activate units" />
		<app-filter :value="hasFilters" @click="applyFilters()">
			<app-row no-padding>
				<app-cell cols="6" sm="4">
					<input-text
						v-model="filters.code"
						label="Unit code"
						clearable
						dense
					></input-text>
				</app-cell>
				<app-cell cols="6" sm="4">
					<input-select
						v-model="filters.complex"
						label="Complex"
						:items="complexes"
						clearable
						dense
					></input-select>
				</app-cell>
			</app-row>
		</app-filter>
		<app-row>
			<app-cell>
				<div class="mb-4">
					<app-button
						v-if="$ravello.can('website.post')"
						color="success"
						icon="fa-check"
						small
						@click="onActivateSelected()"
						:disabled="!selected || !selected.length"
					>
						Activate
					</app-button>
				</div>
				<app-data-table
					:items="units"
					:headers="headers"
					:total="count"
					:select="$ravello.can('website.post')"
					sort-by="code"
					search
					@select="updateSelection"
					@search="onSearch"
					@update="fetchUnits"
				>
					<template v-slot:actions="{ item }">
						<app-button
							v-if="$ravello.can('website.post')"
							color="success"
							icon="fa-check"
							table
							:loading="loading[item._id]"
							:disabled="activated[item._id] || (item.require_rental_license && !item.rental_license)"
							@click="onActivate(item._id)"
						>
							Activate
						</app-button>
					</template>
				</app-data-table>
			</app-cell>
		</app-row>
	</app-page>
</template>

<script>
import { mapAll } from '@/utils/async'
import { filters, search } from '@/mixins'

export default {
	mixins: [filters, search],
	data() {
		return {
			website: {},
			units: [],
			selected: [],
			activated: {},
			loading: {},
			map: {},
			count: -1,
			headers: [{
				text: 'Code',
				value: 'code',
				sortable: true,
			}, {
				text: 'Name',
				value: 'name',
				sortable: true,
			}, {
				text: 'Rental License Required',
				value: 'require_rental_license',
				sortable: true,
				cb: item => (item.require_rental_license ? 'Yes' : 'No'),
			}, {
				text: 'Rental License',
				value: 'rental_license',
				sortable: true,
				cb: item => (item.rental_license || '---'),
			}, {
				text: '',
				align: 'end',
				value: 'actions',
				sortable: false,
			}],
			filters: {
				code: '',
				complex: null,
			},
			complexes: [],
		}
	},
	computed: {
		websiteId() {
			return this.$route.params.website
		},
	},
	async mounted() {
		this.website = await this.$ravello.graphql('website', this.websiteId)
			.field('name')
			.query()
		this.complexes = await this.$ravello.graphql('complexes')
			.parent(this.$ravello.graphql('connection')
				.parent(this.$ravello.graphql('website', this.websiteId)))
			.datasource()
			.query()
		this.complexes.sort((a, b) => a.text.localeCompare(b.text))
	},
	methods: {
		async fetchUnits(pagination) {
			const request = this.$ravello.graphql('units')
				.parent(this.$ravello.graphql('connection')
					.parent(this.$ravello.graphql('website', this.websiteId)))
				.fields(['_id', 'name', 'code', 'rental_license', 'require_rental_license'])
				.fromPagination(pagination)

			if (this.appliedFilters.code) {
				request.filter('code', this.appliedFilters.code)
			}
			if (this.appliedFilters.complex) {
				request.filter('complex', this.appliedFilters.complex)
			}
			if (this.count === -1 && !this.searchText) {
				request.count('count')
			}

			if (this.searchText) {
				request.search(this.searchText)
			}
			try {
				this.units = await request.query()
				if (this.searchText) {
					this.count = this.units.length
				}
				if (this.count === -1) {
					this.count = request.results.count
				}

				await this.updateActivated()
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		updateSelection(selected) {
			this.selected = selected
		},

		async updateActivated() {
			const activatedUnits = await this.$ravello.graphql('units')
				.parent(this.$ravello.graphql('website', this.websiteId))
				.fields(['_id', 'active'])
				.include(this.$ravello.graphql('unit').field('_id'))
				.limit(this.units.length)
				.filter('unit', this.units.map(unit => unit._id))
				.query()

			this.activated = activatedUnits
				.reduce((acc, unit) => ({ ...acc, [unit.unit._id]: unit.active }), {})

			this.ids = activatedUnits
				.reduce((acc, unit) => ({ ...acc, [unit.unit._id]: unit._id }), {})
		},

		async onActivate(id) {
			this.loading[id] = true
			try {
				if (typeof this.ids[id] !== 'undefined') {
					await this.$ravello.request()
						.v(2)
						.to('websites')
						.to(this.websiteId)
						.to('units')
						.to(this.ids[id])
						.update({ active: 1 })
				} else {
					await this.$ravello.request()
						.v(2)
						.to('websites')
						.to(this.websiteId)
						.to('units')
						.create({ active: 1, unit: id })
				}
				this.activated = { ...this.activated, [id]: true }
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			delete this.loading[id]
		},

		async onActivateSelected() {
			this.$store.dispatch('app/startLoading')
			await mapAll(this.selected, id => this.onActivate(id))
			this.$store.dispatch('app/stopLoading')
			this.selected = []
		},
	},
}
</script>
